import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/Modal';
import './DocumentUpload.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface FileWithStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error' | 'skipped';
  chunksCount?: number;
  error?: string;
}

const DocumentUpload: React.FC = () => {
  const navigate = useNavigate();
  useAuth(); // Auth context for authentication state
  const [collections, setCollections] = useState<string[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string>('');
  const [newCollectionName, setNewCollectionName] = useState<string>('');
  const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<FileWithStatus[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  // Modal state
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning' | 'loading';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  useEffect(() => {
    fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await fetch(`${API_URL}/collections`);
      const data = await response.json();
      const collectionNames = data.collections || [];
      setCollections(collectionNames);
      if (collectionNames.length > 0 && !selectedCollection) {
        const preferred = collectionNames.find((c: string) => c === 'saucy_hr_docs');
        setSelectedCollection(preferred || collectionNames[0]);
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load collections. Please try again.',
        type: 'error',
      });
    }
  };

  const getCollectionDisplayName = (collection: string) => {
    return collection
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const validateFile = (file: File): boolean => {
    const allowedExtensions = ['.pdf', '.txt', '.md', '.docx', '.doc'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      return false;
    }
    return true;
  };

  const handleFilesSelect = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const allowedExtensions = ['.pdf', '.txt', '.md', '.docx', '.doc'];
    const invalidFiles: string[] = [];
    const validFiles: FileWithStatus[] = [];

    fileArray.forEach((file) => {
      if (!validateFile(file)) {
        invalidFiles.push(file.name);
      } else {
        // Check if file is already in the list
        const isDuplicate = selectedFiles.some(f => f.file.name === file.name && f.file.size === file.size);
        if (!isDuplicate) {
          validFiles.push({ file, status: 'pending' });
        }
      }
    });

    if (invalidFiles.length > 0) {
      setModal({
        isOpen: true,
        title: 'Invalid File Types',
        message: `The following files are not supported: ${invalidFiles.join(', ')}. Supported formats: ${allowedExtensions.join(', ')}`,
        type: 'error',
      });
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelect(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesSelect(e.target.files);
    }
    // Reset input so same files can be selected again
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setModal({
        isOpen: true,
        title: 'No Files Selected',
        message: 'Please select at least one file to upload.',
        type: 'warning',
      });
      return;
    }

    if (!selectedCollection) {
      setModal({
        isOpen: true,
        title: 'No Collection Selected',
        message: 'Please select a collection to upload to.',
        type: 'warning',
      });
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: selectedFiles.length });
    
    // Reset all file statuses to pending
    setSelectedFiles(prev => prev.map(f => ({ ...f, status: 'pending' })));

    const results = {
      success: 0,
      skipped: 0,
      failed: 0,
      totalChunks: 0,
    };

    // Upload files sequentially
    for (let i = 0; i < selectedFiles.length; i++) {
      const fileWithStatus = selectedFiles[i];
      
      // Update status to uploading
      setSelectedFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'uploading' } : f
      ));
      setUploadProgress({ current: i + 1, total: selectedFiles.length });

      try {
        const formData = new FormData();
        formData.append('file', fileWithStatus.file);
        formData.append('collection_name', selectedCollection);

        const response = await fetch(`${API_URL}/documents/add`, {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();

        if (response.ok) {
          results.success++;
          results.totalChunks += data.chunks_count || 0;
          setSelectedFiles(prev => prev.map((f, idx) => 
            idx === i ? { ...f, status: 'success', chunksCount: data.chunks_count } : f
          ));
        } else if (response.status === 409) {
          results.skipped++;
          setSelectedFiles(prev => prev.map((f, idx) => 
            idx === i ? { ...f, status: 'skipped' } : f
          ));
        } else {
          results.failed++;
          setSelectedFiles(prev => prev.map((f, idx) => 
            idx === i ? { ...f, status: 'error', error: data.detail || 'Upload failed' } : f
          ));
        }
      } catch (error: any) {
        results.failed++;
        setSelectedFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error', error: error.message || 'Upload failed' } : f
        ));
      }
    }

    setUploading(false);

    // Show summary modal
    const summaryMessage = [
      `Successfully uploaded: ${results.success}`,
      results.skipped > 0 ? `Skipped (already exists): ${results.skipped}` : '',
      results.failed > 0 ? `Failed: ${results.failed}` : '',
      results.totalChunks > 0 ? `Total chunks created: ${results.totalChunks}` : '',
    ].filter(Boolean).join('\n');

    setModal({
      isOpen: true,
      title: 'Upload Complete',
      message: summaryMessage,
      type: results.failed === 0 ? 'success' : 'warning',
    });
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const getStatusIcon = (status: FileWithStatus['status']) => {
    switch (status) {
      case 'uploading':
        return '⏳';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'skipped':
        return '⏭️';
      default:
        return '📄';
    }
  };

  return (
    <div className="document-upload">
      <div className="upload-header">
        <button
          className="back-button"
          onClick={() => navigate('/admin/dashboard')}
        >
          ← Back to Dashboard
        </button>
        <div>
          <h1>Upload Documents</h1>
          <p>Add documents to the RAG database for AI-powered chat and test generation</p>
        </div>
      </div>

      <div className="upload-container">
        <div className="upload-section">
          <h2>Select Collection</h2>
          {collections.length === 0 || isCreatingNew ? (
            <div className="new-collection-input">
              <input
                type="text"
                className="collection-input"
                placeholder="Enter collection name (e.g., hr_documents)"
                value={newCollectionName}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
                  setNewCollectionName(value);
                  setSelectedCollection(value);
                }}
                disabled={uploading}
              />
              <p className="helper-text">
                Use lowercase letters, numbers, and underscores only. A new collection will be created automatically.
              </p>
              {collections.length > 0 && (
                <button
                  type="button"
                  className="toggle-collection-btn"
                  onClick={() => {
                    setIsCreatingNew(false);
                    setSelectedCollection(collections[0]);
                    setNewCollectionName('');
                  }}
                >
                  ← Select existing collection
                </button>
              )}
            </div>
          ) : (
            <div className="existing-collection-select">
              <select
                className="collection-select"
                value={selectedCollection}
                onChange={(e) => setSelectedCollection(e.target.value)}
                disabled={uploading}
              >
                {collections.map((collection) => (
                  <option key={collection} value={collection}>
                    {getCollectionDisplayName(collection)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="toggle-collection-btn"
                onClick={() => setIsCreatingNew(true)}
              >
                + Create new collection
              </button>
            </div>
          )}
        </div>

        <div className="upload-section">
          <h2>Select Documents</h2>
          <div
            className={`file-drop-zone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              id="file-input"
              type="file"
              accept=".pdf,.txt,.md,.docx,.doc"
              multiple
              onChange={handleFileInputChange}
              disabled={uploading}
              className="file-input-hidden"
            />
            <div className="file-drop-content">
              <div className="drop-icon">📎</div>
              <p className="drop-text">
                Drag and drop documents here, or{' '}
                <label htmlFor="file-input" className="file-input-label">
                  browse
                </label>
              </p>
              <p className="drop-hint">
                Supported formats: PDF, TXT, MD, DOCX, DOC (Multiple files supported)
              </p>
            </div>
          </div>

          {selectedFiles.length > 0 && (
            <div className="files-list-container">
              <div className="files-list-header">
                <span className="files-count">{selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected</span>
                <button
                  className="clear-all-button"
                  onClick={clearAllFiles}
                  disabled={uploading}
                >
                  Clear All
                </button>
              </div>
              <div className="files-list">
                {selectedFiles.map((fileWithStatus, index) => (
                  <div key={index} className={`file-item file-item-${fileWithStatus.status}`}>
                    <div className="file-item-icon">{getStatusIcon(fileWithStatus.status)}</div>
                    <div className="file-item-info">
                      <p className="file-item-name">{fileWithStatus.file.name}</p>
                      <p className="file-item-size">{formatFileSize(fileWithStatus.file.size)}</p>
                      {fileWithStatus.status === 'success' && fileWithStatus.chunksCount !== undefined && (
                        <p className="file-item-chunks">{fileWithStatus.chunksCount} chunks created</p>
                      )}
                      {fileWithStatus.status === 'error' && fileWithStatus.error && (
                        <p className="file-item-error-text">{fileWithStatus.error}</p>
                      )}
                      {fileWithStatus.status === 'skipped' && (
                        <p className="file-item-skipped-text">Already exists in collection</p>
                      )}
                    </div>
                    {fileWithStatus.status === 'pending' && (
                      <button
                        className="remove-file-button"
                        onClick={() => removeFile(index)}
                        disabled={uploading}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {uploading && (
                <div className="upload-progress">
                  <p>Uploading {uploadProgress.current} of {uploadProgress.total} files...</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="upload-actions">
          <button
            className="button-secondary"
            onClick={() => navigate('/admin/dashboard')}
            disabled={uploading}
          >
            Cancel
          </button>
          {(() => {
            const pendingFiles = selectedFiles.filter(f => f.status === 'pending');
            const allProcessed = selectedFiles.length > 0 && pendingFiles.length === 0;
            const successCount = selectedFiles.filter(f => f.status === 'success').length;
            
            if (allProcessed) {
              return (
                <button
                  className="button-primary"
                  onClick={() => {
                    setSelectedFiles([]);
                    setNewCollectionName('');
                  }}
                  style={{ background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)' }}
                >
                  ✓ {successCount} Document{successCount !== 1 ? 's' : ''} Uploaded - Clear & Add More
                </button>
              );
            }
            
            return (
              <button
                className="button-primary"
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0 || !selectedCollection}
              >
                {uploading ? `Uploading... (${uploadProgress.current}/${uploadProgress.total})` : `Upload ${pendingFiles.length} Document${pendingFiles.length !== 1 ? 's' : ''}`}
              </button>
            );
          })()}
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        onClose={modal.type === 'loading' ? undefined : () => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        isLoading={modal.type === 'loading'}
      />
    </div>
  );
};

export default DocumentUpload;
